import React, { useMemo, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight, faTimes } from '@fortawesome/free-solid-svg-icons';
import DualListBox from 'react-dual-listbox';

// iOS Safari and iPad Chrome (same underlying WebKit touch picker) render a
// `<select multiple>` as a compact "X selected" control instead of an inline list,
// hiding every option. There is no CSS fix for this - see
// https://github.com/jakezatecky/react-dual-listbox/issues/138. Fall back to a
// checkbox-based list on iPad only and keep the native DualListBox everywhere else
// (desktop, phones, Android tablets), where it renders fine.
function isIPad() {
  if (typeof navigator === 'undefined') {
    return false;
  }

  const userAgent = navigator.userAgent || navigator.vendor || '';
  if (/iPad/.test(userAgent)) {
    return true;
  }

  // iPadOS 13+ masquerades as desktop Mac Safari; the reliable tell is a
  // "Mac" platform that also reports touch points.
  return navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
}

function flattenOptions(options) {
  return options.reduce((acc, option) => (
    option.options ? acc.concat(flattenOptions(option.options)) : acc.concat([option])
  ), []);
}

function TouchAccessList({ options, selected, onChange, disabled, canFilter }) {
  const [availableFilter, setAvailableFilter] = useState('');
  const [selectedFilter, setSelectedFilter] = useState('');

  const { available, chosen } = useMemo(() => {
    const all = flattenOptions(options);
    const chosenValues = new Set(selected);
    return {
      available: all.filter(option => !chosenValues.has(option.value)),
      chosen: all.filter(option => chosenValues.has(option.value)),
    };
  }, [options, selected]);

  const filterList = (list, term) => (
    !term ? list : list.filter(option => option.label.toLowerCase().includes(term.toLowerCase()))
  );

  const addValue = (value) => { if (!disabled) { onChange([...selected, value]); } };
  const removeValue = (value) => { if (!disabled) { onChange(selected.filter(existing => existing !== value)); } };

  const renderPane = (title, list, term, setTerm, onItemClick, icon) => (
    <Col xs={12} sm={6} className="access-transfer-pane">
      <div className="access-transfer-pane-title">{title}</div>
      {canFilter ? (
        <input
          type="text"
          className="form-control form-control-sm mb-1"
          placeholder="Search..."
          value={term}
          onChange={(event) => setTerm(event.target.value)}
          disabled={disabled}
        />
      ) : null}
      <div className="access-transfer-pane-list">
        {list.length === 0 ? <div className="access-transfer-empty">No items</div> : list.map(option => (
          <button
            type="button"
            key={option.value}
            className="access-transfer-item"
            disabled={disabled || option.disabled}
            onClick={() => onItemClick(option.value)}
          >
            <span>{option.label}</span>
            <FontAwesomeIcon icon={icon} />
          </button>
        ))}
      </div>
    </Col>
  );

  return (
    <Row className="access-transfer-list mx-0">
      {renderPane('Available', filterList(available, availableFilter), availableFilter, setAvailableFilter, addValue, faChevronRight)}
      {renderPane('Access', filterList(chosen, selectedFilter), selectedFilter, setSelectedFilter, removeValue, faTimes)}
    </Row>
  );
}

// Drop-in replacement for react-dual-listbox's DualListBox that is usable on
// iPad. Accepts the same options/selected/onChange/disabled/canFilter props;
// other DualListBox-only props (icons, name, ...) are ignored in iPad mode.
export default function AccessTransferList(props) {
  const [ipadMode] = useState(isIPad);

  if (ipadMode) {
    return <TouchAccessList {...props} />;
  }

  return <DualListBox {...props} />;
}
